import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { matchPath, Route, useLocation } from 'react-router-dom';

import Layout from '../../components/Layout';
import { CartPricePropType } from '../../helpers/prop-types';
import { useCheckGuestSession } from '../../hooks/useCheckGuestSession';
import { useFeaturesConfig } from '../../hooks/useFeaturesConfig';
import { ieShop } from '../../lib/reactives';
import {
  AddressFormPage,
  AddressListPage,
  BFPOFormPage,
  DeliveryMethodPage,
  DeliveryOptionsPage,
  HomeDeliveryPage,
  LocalShopConfirmationPage,
  LocalShopDetailsPage,
  LocalShopSearchPage,
  ReviewAndPayPage,
  StoreDetailsPage,
  StoreErrorPage,
  StoreSearchPage,
} from '../../pages';
import StoreConfirmationPage from '../../pages/StoreConfirmationPage';
import { Routes } from '../routes';
import RoutesWrapper from '../RoutesWrapper';

const GUEST_PROTECTED_ROUTES = [
  Routes.homeDelivery.path,
  Routes.selectAddress.path,
  Routes.addressForm.path,
  Routes.bfpoForm.path,
  Routes.localShopSearch.path,
  Routes.localShopDetails.path,
  Routes.localShopConfirm.path,
  Routes.storeSearch.path,
];

const MainRoutes = ({ cart, loading, error }) => {
  const location = useLocation();
  const { checkGuestSession } = useCheckGuestSession();

  useFeaturesConfig('IE', {
    onCompleted: ({ featuresConfig }) => {
      const found = Array.from(featuresConfig)?.find(
        (f) => f.name === 'ShowLocalShopForWeb',
      )?.enabled;
      ieShop({ ShowLocalShopForWebIE: found });
    },
  });

  useEffect(() => {
    if (!cart?.isGuest) return;

    const isProtectedRoute = GUEST_PROTECTED_ROUTES.some((route) =>
      matchPath(route, location.pathname),
    );

    if (isProtectedRoute) {
      checkGuestSession();
    }
  }, [location.pathname, cart?.isGuest, checkGuestSession]);

  return (
    <RoutesWrapper>
      <Route path={Routes.storeError.path} element={<StoreErrorPage />} />
      <Route
        path=""
        element={<Layout cart={cart} loading={loading} error={error} />}
      >
        <Route
          path={Routes.deliveryType.path}
          element={
            <DeliveryMethodPage
              analytics={Routes.deliveryType.analytics}
              country={cart?.locale?.country}
            />
          }
        />
        <Route
          path={Routes.homeDelivery.path}
          element={
            <HomeDeliveryPage analytics={Routes.homeDelivery.analytics} />
          }
        />
        <Route
          path={Routes.selectAddress.path}
          element={
            <AddressListPage analytics={Routes.selectAddress.analytics} />
          }
        />
        <Route
          path={Routes.addressForm.path}
          element={
            <AddressFormPage
              country={cart?.locale?.country}
              billingAddress={cart?.billingAddress}
              analytics={Routes.addressForm.analytics}
              cart={cart}
            />
          }
        />
        <Route
          path={Routes.bfpoForm.path}
          element={
            <BFPOFormPage analytics={Routes.bfpoForm.analytics} cart={cart} />
          }
        />
        <Route
          path={Routes.storeSearch.path}
          element={<StoreSearchPage analytics={Routes.storeSearch.analytics} />}
        />
        <Route
          path={Routes.storeDetails.path}
          element={
            <StoreDetailsPage analytics={Routes.storeDetails.analytics} />
          }
        />
        <Route
          path={Routes.storeConfirm.path}
          element={
            <StoreConfirmationPage analytics={Routes.storeConfirm.analytics} />
          }
        />
        <Route
          path={Routes.localShopSearch.path}
          element={
            <LocalShopSearchPage analytics={Routes.localShopSearch.analytics} />
          }
        />
        <Route
          path={Routes.localShopDetails.path}
          element={
            <LocalShopDetailsPage
              analytics={Routes.localShopDetails.analytics}
            />
          }
        />
        <Route
          path={Routes.localShopConfirm.path}
          element={
            <LocalShopConfirmationPage
              analytics={Routes.localShopConfirm.analytics}
            />
          }
        />
        <Route
          path={Routes.deliveryOptions.path}
          element={
            <DeliveryOptionsPage
              analytics={Routes.deliveryOptions.analytics}
              cart={cart}
            />
          }
        />
        <Route
          path={Routes.reviewAndPay.path}
          element={
            <ReviewAndPayPage analytics={Routes.reviewAndPay.analytics} />
          }
        />
      </Route>
    </RoutesWrapper>
  );
};

MainRoutes.propTypes = {
  cart: PropTypes.shape({
    customerId: PropTypes.string.isRequired,
    discountCodes: PropTypes.arrayOf(PropTypes.string),
    discountList: PropTypes.arrayOf(PropTypes.shape()),
    subtotal: CartPricePropType.isRequired,
    total: CartPricePropType.isRequired,
    remaining: CartPricePropType,
    totalLineItems: PropTypes.number.isRequired,
    lineItems: PropTypes.arrayOf(PropTypes.shape()),
    paymentMethod: PropTypes.string,
    shippingAddress: PropTypes.shape(),
    billingAddress: PropTypes.shape(),
    deliveryOption: PropTypes.shape(),
    locale: PropTypes.shape({
      country: PropTypes.string,
    }),
    isGuest: PropTypes.bool,
  }).isRequired,
  error: PropTypes.shape(),
  loading: PropTypes.bool,
};

MainRoutes.defaultProps = {
  loading: false,
  error: undefined,
};
export default MainRoutes;
